import { type CategoriaArbitralTemplate } from '@/types/arbitral/categoriaArbitralTemplate';
import type { AnexoPendencia, RastroPendenciaEntry } from '@/features/secretaria-arbitral';

export type ProcessoArbitralDetailStatus =
  | 'PROCESSO_DISTRIBUIDO'
  | 'TRIAGEM_EM_ANDAMENTO'
  | 'TRIAGEM_CONCLUIDA'
  | 'EM_CITACAO'
  | 'EM_ANDAMENTO'
  | 'SENTENCA_EMITIDA'
  | 'CONCLUIDO'
  | 'NEGADO'
  | 'CANCELADO'
  | 'INDEFINIDO';

export type ProcessoArbitralMetodo = 'Arbitragem' | 'Mediação';

export type ProcessoArbitralRito = 'Ordinário' | 'Sumário' | 'Sumaríssimo' | 'Por Árbitro' | string;

export type TipoParte =
  | 'REQUERENTE'
  | 'REQUERIDO'
  | 'ADVOGADO_REQUERENTE'
  | 'ADVOGADO_REQUERIDO'
  | 'REPRESENTANTE_REQUERENTE'
  | 'REPRESENTANTE_REQUERIDO';

export type StatusAcessoPlataforma = 'CADASTRADO' | 'NUNCA_ACESSOU' | 'BLOQUEADO' | 'SEM_CADASTRO';

export type StatusNotificacao = 'ENTREGUE' | 'LIDA' | 'BOUNCE' | 'PENDENTE' | 'NAO_ENVIADA';

export type TipoPendencia =
  | 'DOCUMENTACAO'
  | 'PAGAMENTO'
  | 'MANIFESTACAO'
  | 'ESCLARECIMENTO'
  | 'OUTROS'
  | 'CORRECAO_DADO'
  | 'HABILITACAO_ADVOGADO'
  | 'ASSINATURA'
  | 'INFORMACAO'
  | 'CONCLUSO'
  | 'CONCLUSO_RESPONDIDO'
  | 'TERMO_IMPARCIALIDADE'
  | 'IMPUGNACAO_ARBITRO'
  | 'PRAZO_IMPUGNACAO_ARBITRO'
  | 'KYC'
  | 'REGULARIZACAO_TRIAGEM'
  | 'OFICIO_REQUISITORIO';

export type StatusPendencia =
  | 'PENDENTE'
  | 'AGUARDANDO_VALIDACAO'
  | 'RESOLVIDA'
  | 'REJEITADA'
  | 'EXPIRADA'
  | 'ENCERRADA_PELA_SENTENCA';

export type SolicitantePendencia = 'SECRETARIA' | 'SISTEMA' | 'ARBITRO';

export type DestinatarioPendencia = 'REQUERENTE' | 'REQUERIDO' | 'ARBITRO' | 'SECRETARIA';

export type StatusFase = 'CONCLUIDA' | 'EM_ANDAMENTO' | 'AGUARDANDO';

export type ResponsavelFase = 'EMPRESA' | 'REQUERIDO' | 'SECRETARIA' | 'ARBITRO' | 'SISTEMA';

export type TipoEventoAndamento =
  | 'CRIACAO'
  | 'STATUS_ALTERADO'
  | 'DOCUMENTO_ANEXADO'
  | 'MENSAGEM_ENVIADA'
  | 'PENDENCIA_CRIADA'
  | 'PENDENCIA_RESOLVIDA'
  | 'FASE_CONCLUIDA'
  | 'NOTIFICACAO_ENVIADA'
  | 'PARTE_ADICIONADA'
  | 'DECISAO'
  | 'MANUAL'
  | 'TRIAGEM_INICIADA'
  | 'TRIAGEM_APROVADA'
  | 'TRIAGEM_NEGADA'
  | 'INTIMACAO_DOCUMENTO_FALTANTE'
  | 'DOCUMENTO_RECEBIDO'
  | 'CITACAO_ELETRONICA_ENVIADA'
  | 'AR_ENVIADO'
  | 'AR_RETORNADO'
  | 'MANIFESTACAO_REGISTRADA'
  | 'MANIFESTACAO_DESCONSIDERADA'
  | 'CONCLUSO_FEITO'
  | 'DECURSO_PUBLICADO'
  | 'SORTEIO_ARBITRO'
  | 'TERMO_IMPARCIALIDADE_JUNTADO'
  | 'TERMO_ARBITRAGEM_PUBLICADO'
  | 'SENTENCA_PUBLICADA'
  | 'INTIMACAO_SENTENCA_ENVIADA'
  | 'EMBARGOS_RECEBIDOS'
  | 'TRANSITO_PUBLICADO'
  | 'PAGAMENTO_CONFIRMADO'
  | 'PAGAMENTO_VENCIDO'
  | 'OFICIO_TERCEIRO_ENVIADO'
  | 'OFICIO_TERCEIRO_RESPONDIDO'
  | 'KYC_SOLICITADO'
  | 'KYC_VALIDADO'
  | 'KYC_RECUSADO'
  | 'ALTERACAO_DADO_CADASTRO'
  | 'BLOQUEIO_OVERRIDE_REGISTRADO'
  | 'TRIAGEM_DEVOLVIDA'
  | 'PENDENCIA_RESPONDIDA';

export type TipoMensagemChat = 'TEXTO' | 'ARQUIVO' | 'SISTEMA';

export type PapelNovaParte = 'REQUERENTE' | 'REQUERIDO' | 'ADVOGADO' | 'REPRESENTANTE';

export type LadoRepresentado = 'requerente' | 'requerido';

export interface EstadoIdentificacaoProcesso {
  valorCausa: number | null;
  rito: string | null;
  pedidoLiminar: boolean;
  descricaoUrgencia: string | null;
}

// TODO(#774): promover para src/types/ — shape idêntico a AlteracaoSolicitada de secretaria-arbitral
export interface AlteracaoExibida {
  campo: string;
  de: string | null;
  para: string;
}

export interface DadosInclusaoParte {
  papel: PapelNovaParte;
  tipo: 'PF' | 'PJ';
  nome: string;
  cpfCnpj: string;
  telefone: string;
  email: string;
  parteVinculada: string;
  parteVinculadaNome: string;
  oab: string;
  ufOab: string;
  carteiraOab: File | null;
  procuracao: File | null;
}

export interface CreateParteProcessoPayload {
  papel: PapelNovaParte;
  nome: string;
  cpfCnpj: string;
  email?: string;
  telefone?: string;
  tipo: 'PF' | 'PJ';
  ladoRepresentado?: LadoRepresentado;
  oab?: string;
  ufOab?: string;
  representacaoDeclarada?: boolean;
}

export interface ParteProcesso {
  id: string;
  nomeCompleto: string;
  cpfCnpj: string;
  tipo: TipoParte;
  /** "UF NUMERO" (formato do backend); null quando a pessoa não tem OAB (86ak7gd2z). */
  oab: string | null;
  email: string;
  telefone: string;
  statusAcesso: StatusAcessoPlataforma;
  statusUltimaNotificacao: StatusNotificacao;
  contatoInvalido: boolean;
  motivoBounce?: string;
  isCurrentUser: boolean;
  userId: string | null;
}

export interface PendenciaProcesso {
  id: string;
  tipo: TipoPendencia;
  titulo: string;
  descricao: string;
  solicitante: SolicitantePendencia;
  destinatario?: DestinatarioPendencia | null;
  dataSolicitacao: string;
  prazoVencimento: string | null;
  diasRestantes: number;
  status: StatusPendencia;
  motivoRejeicao?: string;
  andamentoRespostaId?: string;
  // Mesmo shape de anexo/rastro da Secretaria/árbitro (86ak5xmzp) — reuso, não cópia.
  anexos: AnexoPendencia[];
  rastro: RastroPendenciaEntry[];
  /**
   * Espelha `assertPodeResponderPendencia` do backend (inclui o bypass do criador do processo).
   * Substitui `isParteAtiva` como gate de "Responder" por pendência — `isParteAtiva` não sabia
   * do bypass e escondia o formulário mesmo quando o backend aceitaria o envio (86ak6ehy6).
   */
  podeResponderPendencia: boolean;
}

export type ProcessoArbitralDetailCru = Omit<ProcessoArbitralDetail, 'pendencias'> & {
  pendencias?: (Omit<PendenciaProcesso, 'titulo'> & { titulo?: string | null })[];
};

export interface FaseProcesso {
  id: string;
  nome: string;
  status: StatusFase;
  dataInicio?: string;
  dataConclusao?: string;
  executor?: string;
  prazoVigente?: string;
  dataVencimentoPrazo?: string;
  responsavelAtual?: ResponsavelFase | null;
}

export type ProcessoArbitralDocumentoCategoria = 'comprovacao' | 'conflito' | 'andamento';
export type ProcessoArbitralDocumentoSubtipo =
  | 'PROCURACAO'
  | 'CARTEIRA_OAB'
  | 'CONTRATO_SOCIAL'
  | 'ANEXO_CONFLITO';

export interface DocumentoCriadoPor {
  id: string;
  nome: string;
  email: string;
}

export interface DocumentoProcesso {
  id: string;
  categoria: ProcessoArbitralDocumentoCategoria;
  subtipo: ProcessoArbitralDocumentoSubtipo | null;
  tipoDocumento: string;
  nomeArquivo: string;
  nomeOriginal: string;
  tamanhoArquivo: number;
  tipoMime: string;
  descricao: string | null;
  sigilo: boolean;
  obrigatorio: boolean;
  ordemExibicao: number;
  uploadedAt: string;
  requirementId: string | null;
  partyId: string | null;
  partyName: string | null;
  partyRole: string | null;
  representedSide: 'requerente' | 'requerido' | null;
  downloadEndpoint: string;
  thumbnailEndpoint: string | null;
  criadoPor: DocumentoCriadoPor | null;
}

export type ResponsavelAndamento = 'SECRETARIA' | 'ESPECIALISTAS' | 'TODOS' | string;

export interface AnexoAndamento {
  id: string;
  nome: string;
  tamanhoBytes: number;
  url?: string;
}

// Papel/origem de quem registrou o andamento (vem do back). "Notificante" = requerente;
// "Notificada" = requerido.
export type PapelAutorAndamento =
  | 'SECRETARIA'
  | 'ARBITRO'
  | 'PARTE_NOTIFICANTE'
  | 'PARTE_NOTIFICADA'
  | 'SISTEMA';

export interface AndamentoProcesso {
  id: string;
  dataHora: string;
  tipo: TipoEventoAndamento;
  descricao: string;
  autor: string;
  autorId?: string | null;
  papel?: PapelAutorAndamento;
  responsaveis?: ResponsavelAndamento[];
  prazoFinal?: string;
  isContestacao?: boolean;
  anexos?: AnexoAndamento[];
}

/** Contrato de `useExportarHistorico`. */
export interface ExportarHistoricoResultado {
  exportar: () => Promise<void>;
  exportando: boolean;
}

export type AutorTipoChat = TipoParte | 'SECRETARIA' | 'SISTEMA' | 'ARBITRO';

export interface MensagemChatItem {
  id: string;
  dataHora: string;
  tipo: TipoMensagemChat;
  texto: string;
  autorNome: string;
  autorTipo: AutorTipoChat;
  autorUsuarioId: string;
  lida: boolean;
  arquivosAnexados?: ArquivoAnexadoChat[];
}

export interface ArquivoAnexadoChat {
  id: string;
  nome: string;
  url: string;
  tamanhoBytes: number;
  mimeType: string;
}

export interface TypingUser {
  userId: string;
  autorNome: string;
  autorTipo: string;
  timestamp: string;
}

export interface ProcessoArbitralDetail {
  id: string;
  codigo: string;
  dataAbertura: string;
  status: ProcessoArbitralDetailStatus;
  camaraArbitral: string;
  tipoRito: ProcessoArbitralRito;
  metodoSolucao: ProcessoArbitralMetodo;
  valorCausa: number;
  pedidoLiminar: boolean;
  descricaoUrgencia?: string;
  numeroContrato?: string;
  breveRelato?: string;
  assistenciaPedido?: string;
  partes: ParteProcesso[];
  pendencias: PendenciaProcesso[];
  totalPendenciasAtivas: number;
  fases: FaseProcesso[];
  documentos: DocumentoProcesso[];
  andamentos: AndamentoProcesso[];
  totalMensagensNaoLidas: number;
  certificados: CertificadoGerado[];
  situacoes: SituacaoAtiva[];
  especialistas: Especialista[];
}

export interface SituacaoAtiva {
  id: string;
  key: string;
  label: string;
  statusPai: string | null;
}

export type PapelEspecialista = 'SECRETARIA' | 'ARBITRO';

export interface Especialista {
  id: string;
  nome: string;
  papel: PapelEspecialista;
  cargo?: string;
  /**
   * O prazo de impugnação deste árbitro ainda corre — regra do core (`arbitrosComImpugnacaoNoPrazo`),
   * que também recusa a solicitação fora dele. `false` sem prazo nenhum (ClickUp 86akqegh9).
   */
  impugnacaoNoPrazo: boolean;
}

export type TipoSolicitacaoAlteracao =
  | 'INCLUSAO_PARTE'
  | 'ADICIONAR_ADVOGADO'
  | 'ALTERACAO_PARTE'
  | 'EXCLUSAO_PARTE'
  | 'ALTERACAO_DADOS'
  | 'ALTERACAO_BREVE_RELATO'
  | 'ALTERACAO_PEDIDO'
  | 'IMPUGNACAO_ARBITRO'
  | 'CANCELAMENTO';

export type StatusSolicitacaoAlteracao = 'SOLICITADA' | 'APROVADA' | 'REJEITADA' | 'CANCELADA';

// Solicitação de alteração do cliente → Secretaria (espelha o back).
export interface SolicitacaoAlteracao {
  id: string;
  tipo: TipoSolicitacaoAlteracao;
  status: StatusSolicitacaoAlteracao;
  alvoParteArbitralId: string | null;
  alvoArbitroProcessoId: string | null;
  justificativa: string | null;
  respostaSecretaria: string | null;
  dataCriacao: string;
}

export interface CriarSolicitacaoPayload {
  tipo: TipoSolicitacaoAlteracao;
  payload?: Record<string, unknown>;
  justificativa?: string;
  alvoParteArbitralId?: string;
  alvoArbitroProcessoId?: string;
  anexos?: File[];
}

export interface CancelarProcessoResultado {
  modo: 'DIRETO' | 'SOLICITACAO';
  status: string;
  solicitacao: SolicitacaoAlteracao | null;
}

export const ABAS_DETALHE_ARBITRAL = [
  'resumo',
  'pendencias',
  'documentos',
  'envio-fisico',
  'financeiro',
  'andamentos',
  'chat',
  'certificados',
] as const;

export type ArbitralDetailTab = (typeof ABAS_DETALHE_ARBITRAL)[number];

export type TipoCertificado =
  | 'COMPROVANTE_PROTOCOLO'
  | 'CERTIFICADO_ACESSO'
  | 'CERTIFICADO_SENTENCA'
  | 'CERTIFICADO_INTIMACAO'
  | 'CERTIFICADO_PAGAMENTO'
  | 'COMPROVANTE_NOTIFICACAO'
  | 'TERMO_IMPARCIALIDADE'
  | 'TERMO_ARBITRAGEM';

export const TIPO_CERTIFICADO_LABEL: Record<TipoCertificado, string> = {
  COMPROVANTE_PROTOCOLO: 'Comprovante de protocolo',
  CERTIFICADO_PAGAMENTO: 'Comprovante de pagamento',
  COMPROVANTE_NOTIFICACAO: 'Comprovante de notificação',
  CERTIFICADO_ACESSO: 'Certificado de acesso',
  CERTIFICADO_INTIMACAO: 'Comprovante de intimação',
  CERTIFICADO_SENTENCA: 'Certificado de sentença',
  TERMO_IMPARCIALIDADE: 'Termo de Imparcialidade',
  TERMO_ARBITRAGEM: 'Termo de Arbitragem',
};

export function isTipoCertificado(valor: string | null): valor is TipoCertificado {
  return valor !== null && Object.prototype.hasOwnProperty.call(TIPO_CERTIFICADO_LABEL, valor);
}

export interface CertificadoGerado {
  id: string;
  tipo: TipoCertificado;
  /**
   * Categoria do modelo que gerou o documento — o discriminante REAL, porque `tipo` agrupa atos
   * diferentes sob o mesmo valor (`CERTIFICADO_INTIMACAO` sai da nomeação e do decurso). `null`
   * no punhado de certificados que não nascem de template (comprovante de protocolo, Documento
   * da Sentença), em que quem identifica continua sendo `tipo`.
   *
   * ⚠️ Chega por cast cru em `store/certificados/services.ts` — valide com
   * `isCategoriaArbitralTemplate` antes de tratar como conhecida.
   */
  categoria?: CategoriaArbitralTemplate | null;
  nome: string;
  dataEmissao: string;
  destinatario?: string;
  url: string | null;
  tamanhoBytes?: number | null;
  status?: 'PENDENTE' | 'GERADO' | 'ERRO';
  /**
   * Cancelamento formal (rn9). `status` continua `GERADO` numa certidão cancelada — o
   * discriminante é esta data, não o enum (decisão registrada no ADR do discriminante no core).
   */
  canceladoEm?: string | null;
  motivoCancelamento?: string | null;
}
